// src/layouts/Pages/PageHeader/ActionButton.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import React, { useMemo } from 'react';
import { Button, Dropdown, Tooltip, Space, theme } from 'antd';
import translateX from 'utils/translateX';
import { MoreOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useModuleContext } from 'components/core/ModuleProvider';
import { useSimpleFormContext } from 'components/SimpleComponents/SimpleFormProvider';
import { useListContext } from 'components/core/ListProvider';
import useZustand from 'hooks/useZustand';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const ActionButton = ({
  // Identification
  action,

  // Primary Button
  label,
  tooltip,
  icon,
  type = 'primary',
  disabled,
  hideButton = false,
  onClick,

  // Dropdown
  dropdownIcon,
  dropdownTooltip,
  dropdownType,
  dropdownDisabled,
  dropdownItems = [],

  // Context (passed by ActionsButton)
  actions = [],
  actionContext,
  _actionConfig,
}) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const moduleContext = useModuleContext();
  const {
    setDocumentDuplicated,
    setDocumentState,
    documentState,
    handleArchiveDocuments,
    handleRemoveDocuments,
    handleRestoreDocuments,
  } = moduleContext;

  // Get form-related values from SimpleFormContext (may be undefined if not inside SimpleFormProvider)
  // IMPORTANT: ActionButton may be OUTSIDE the SimpleFormProvider tree (e.g., in PageHeader)
  // In that case, useSimpleFormContext() returns {}, so we fallback to moduleContext.formContext
  // which is registered by SimpleFormProvider via registerProviderContext('formContext', ...)
  const directFormContext = useSimpleFormContext();
  const formContext = directFormContext?.form ? directFormContext : moduleContext?.formContext;
  const submitForm = formContext?.submitForm;
  const resetForm = formContext?.resetForm;
  const locked = formContext?.locked;
  const submitting = formContext?.submitting;
  const working = formContext?.working;

  // Get list-related values from ListContext (may be undefined if not inside ListProvider)
  // These are used for actions like "Clear Filters" in dropdown menus
  const listContext = useListContext();
  const { hasActiveFilters, clearFilters, refresh } = listContext || {};

  const storedCurrentPage = useZustand('module', 'currentPage');
  const setPageStore = useZustand('module', 'setPage');
  const storedListSettings = useZustand('module', 'listSettings');
  const storedPageSettings = useZustand('module', 'pageSettings');
  const storedSubscription = useZustand('auth', 'selectedSubscription');

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const fallbackNavigate = useNavigate();
  const { token } = theme.useToken();

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const navigate = actionContext?.navigate || fallbackNavigate;

  // Check if we're in a DocumentContext or ListContext
  const isDocumentContext = !!moduleContext?.documentContext;
  const isListContext = !!moduleContext?.listContext;

  const isViewMode = documentState?.mode === 'view';

  // localDisabled logic:
  // - In DocumentContext: consider isViewMode, locked, submitting, working
  // - In ListContext: only consider working state
  const localDisabled = isDocumentContext
    ? isViewMode || !!locked || !!submitting || !!working
    : !!working;

  // respect external and local states
  const isPrimaryDisabled =
    typeof disabled === 'function'
      ? disabled({ page: storedCurrentPage, moduleContext })
      : Boolean(disabled) || localDisabled;

  const isDropdownDisabled =
    typeof dropdownDisabled === 'function'
      ? dropdownDisabled({ page: storedCurrentPage, moduleContext })
      : Boolean(dropdownDisabled) || isPrimaryDisabled;

  // Build menu items from dropdownItems (no native items - all from module config)
  const menuItems = useMemo(
    () => buildMenuItems(dropdownItems, storedCurrentPage, moduleContext, { hasActiveFilters, storedSubscription }),
    [dropdownItems, storedCurrentPage, moduleContext, hasActiveFilters, storedSubscription]
  );

  const hasAnyDropdown = menuItems.length > 0;
  const showActionsFlag = storedPageSettings?.showActionsButton;
  const allowSplit = showActionsFlag !== false;

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────

  // Build context object to pass to handlers
  const buildHandlerContext = () => ({
    // Functions from ModuleContext
    submit: submitForm,
    navigate,
    resetForm,
    setDocumentState,
    setDocumentDuplicated,
    handleArchiveDocuments,
    handleRemoveDocuments,
    handleRestoreDocuments,

    // Functions from ListContext (for list page actions)
    hasActiveFilters,
    clearFilters,
    refresh,

    // Current state
    page: storedCurrentPage,
    listSettings: storedListSettings,
    pageSettings: storedPageSettings,
    documentState,
    storedSubscription,

    // Utilities
    setPage: setPageStore,
    config: _actionConfig,
    actionContext,
    moduleContext,
  });

  const handleMenuClick = ({ key }) => {
    const item = (dropdownItems || []).find(i => i && i.key === key);
    if (item && typeof item.onClick === 'function') {
      try {
        item.onClick(buildHandlerContext());
      } catch {}
    }
  };

  const handlePrimaryClick = () => {
    if (typeof onClick === 'function') {
      try {
        onClick(buildHandlerContext());
      } catch {}
      return;
    }
    // Default behavior: submit form (can be overridden by onClick in module config)
    submitForm?.();
  };

  // Prevent blur from stealing focus before click registers
  // This fixes the "first click doesn't work" issue when clicking Save while an input is focused
  const handleMouseDown = e => {
    e.preventDefault();
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────
  const primaryLabel = translateX(label || action || 'action');
  const primaryType = type || 'primary';
  const rightType = dropdownType || primaryType;

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. UI
  // ─────────────────────────────────────────────────────────────────────────────

  // Case A: hide button and there IS dropdown -> only dropdown trigger
  if (hideButton && hasAnyDropdown) {
    return (
      <Dropdown menu={{ items: menuItems, onClick: handleMenuClick }} placement="bottomRight">
        <Tooltip title={translateX(dropdownTooltip || 'actions')}>
          <Button
            type={rightType}
            style={{ backgroundColor: isDropdownDisabled ? '#ffffff' : undefined }}
            disabled={isDropdownDisabled}
            icon={dropdownIcon || <MoreOutlined />}
            title={translateX('actions')}
          />
        </Tooltip>
      </Dropdown>
    );
  }

  // Case B: no dropdown at all -> only primary button
  if (!allowSplit || !hasAnyDropdown) {
    return (
      <Tooltip title={translateX(tooltip || action || 'action')}>
        <Button
          type={primaryType}
          icon={icon}
          onClick={handlePrimaryClick}
          onMouseDown={handleMouseDown}
          disabled={isPrimaryDisabled}
        >
          {primaryLabel}
        </Button>
      </Tooltip>
    );
  }

  // Case C: both primary and dropdown present -> split button with Space.Compact
  return (
    <Space.Compact>
      <Tooltip title={translateX(tooltip || action || 'action')}>
        <Button
          type={primaryType}
          icon={icon}
          onClick={handlePrimaryClick}
          onMouseDown={handleMouseDown}
          disabled={isPrimaryDisabled}
          style={
            isPrimaryDisabled
              ? {
                  borderColor: token.colorBorder,
                }
              : undefined
          }
        >
          {primaryLabel}
        </Button>
      </Tooltip>
      <Dropdown
        menu={{ items: menuItems, onClick: handleMenuClick }}
        trigger={['click']}
        placement="bottomRight"
      >
        <Tooltip title={translateX(dropdownTooltip || 'actions')}>
          <Button
            type={rightType}
            icon={dropdownIcon || <MoreOutlined />}
            disabled={isDropdownDisabled}
            style={
              isDropdownDisabled
                ? {
                    borderColor: token.colorBorder,
                    color: token.colorTextDisabled,
                  }
                : undefined
            }
          />
        </Tooltip>
      </Dropdown>
    </Space.Compact>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default ActionButton;

// *******************************************************************************
// HELPERS
// *******************************************************************************
function buildMenuItems(dropdownItems, currentPage, moduleContext, extraContext = {}) {
  if (!Array.isArray(dropdownItems) || dropdownItems.length === 0) return [];

  // Context object passed to disabled/hidden functions
  const ctx = { currentPage, moduleContext, ...extraContext };

  // First pass: normalize and filter items
  const normalizedItems = dropdownItems
    .map(i => {
      // Normalize string 'divider' to object
      if (i === 'divider') return { type: 'divider' };
      return i;
    })
    .filter(i => {
      // Keep dividers
      if (i && i.type === 'divider') return true;
      // Filter out non-objects and items without keys
      if (!i || typeof i !== 'object' || !i.key) return false;
      // Filter out hidden items
      const isHidden = typeof i.hidden === 'function' ? i.hidden(ctx) : !!i.hidden;
      return !isHidden;
    });

  // Second pass: remove orphan dividers (at start, end, or consecutive)
  const cleanedItems = normalizedItems.filter((item, index, arr) => {
    if (item.type !== 'divider') return true;
    // Check if there's a non-divider item above
    const hasItemAbove = arr.slice(0, index).some(i => i.type !== 'divider');
    // Check if there's a non-divider item below
    const hasItemBelow = arr.slice(index + 1).some(i => i.type !== 'divider');
    return hasItemAbove && hasItemBelow;
  });

  // Third pass: map to final format
  return cleanedItems.map(i => {
    if (i.type === 'divider') return { type: 'divider' };

    const isDanger = !!i.danger;
    const baseLabel = translateX(i.label) || i.label;

    // If disabled is a function, evaluate in real time
    // Include extraContext values for actions like "Clear Filters"
    let isDisabled =
      typeof i.disabled === 'function' ? i.disabled(ctx) : !!i.disabled;

    const iconNode = i.icon ? React.cloneElement(i.icon, {}) : undefined;

    return {
      key: String(i.key),
      label: baseLabel,
      icon: iconNode,
      disabled: isDisabled,
      danger: isDanger,
    };
  });
}
